<template>
  <div>
    <v-layout v-resize="onResize" column style="">
      <v-dialog v-model="urlDialog" width="600" :overlay-opacity="0.8" :overlay-color="overlayColor" content-class="plain-dialog">
        <v-card flat>
          <v-card-text class="pa-7">
            <div class="modal-title">
              <h4>Client URLs</h4>
            </div>
            <div class="modal-body">
              <div v-if="clientUrls">
                <div v-if="clientUrls.primaryUrl" class="url-item">
                  <div class="url-label">Primary Contact URL:</div>
                  <div class="url-content">
                    <span class="url-text">{{ clientUrls.primaryUrl }}</span>
                    <v-btn small text color="primary" @click="copyUrl(clientUrls.primaryUrl)">
                      <v-icon small>fas fa-copy</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div v-if="clientUrls.secondaryUrl" class="url-item mt-4">
                  <div class="url-label">Secondary Contact URL:</div>
                  <div class="url-content">
                    <span class="url-text">{{ clientUrls.secondaryUrl }}</span>
                    <v-btn small text color="primary" @click="copyUrl(clientUrls.secondaryUrl)">
                      <v-icon small>fas fa-copy</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div v-if="!clientUrls.primaryUrl && !clientUrls.secondaryUrl" class="no-urls">
                  No URLs returned.
                </div>
              </div>
            </div>
            <div class="modal-actions">
              <a @click="closeUrlDialog()" class="btn secondary mt-3">Close</a>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
      <!-- :items-per-page="-1" is meant to allow "manual" pagination -->
      <v-data-table
        show-select
        v-model="selected"
        :headers="headers"
        :items="clients"
        :items-per-page="pagination.itemsPerPage"
        :server-items-length="pagination.itemsLength"
        :hide-default-header="isMobile"
        :options.sync="pagination"
        class="responsive-table client-table"
        :class="{ mobile: isMobile }"
        :loading="tableLoading"
        ref="clientTable"
        :footer-props="{
          'items-per-page-options': [15, 30]
        }"
      >
        <template v-slot:top>
          <v-row>
            <v-col>
              <v-text-field
                outlined
                type="text"
                maxlength="256"
                placeholder="Search Clients..."
                v-model.trim="search"
                :backgroundColor="inputBackgroundColor"
                clearable
                class="search-input mb-2"
                single-line
                hide-details
                ref="searchClients"
                @click:clear="resetItems()"
              >
                <template v-slot:append>
                  <span
                    class="search"
                    v-if="!search || search.length === 0"
                    :key="tableLoading"
                  >
                    <v-icon small>fas fa-search</v-icon>
                  </span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row class="filter-action-row align-center">
            <v-col class="status-col">
              <v-select
                v-model="filterStatus"
                :items="statuses"
                item-text="text"
                item-value="value"
                :backgroundColor="inputBackgroundColor"
                outlined
                placeholder="Status"
                ref="statusFilter"
                hide-details
                dense
              >
                <template v-slot:prepend-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title
                        class="prepend"
                        @click="clearStatusFilter()"
                      >
                        Clear Filter
                        <a class="close">
                          <div><i class="fas fa-times"></i></div>
                        </a>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
            <v-col class="o-status-col">
              <v-btn-toggle v-model="fetchMode" mandatory @change="getClients">
                <v-btn :value="fetchStatuses.ACTIVE">Active</v-btn>
                <v-btn :value="fetchStatuses.ARCHIVED">Archived</v-btn>
                <v-btn :value="fetchStatuses.DRAFT">Draft</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col class="button-area">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    rounded
                    v-show="selected.length > 0"
                    v-bind="attrs"
                    v-on="on"
                    class="batch-activator"
                    >Batch ({{ selected.length }})
                    <span class="fa-icon"
                      ><i class="fas fa-ellipsis-v"></i></span
                  ></v-btn>
                </template>
                <v-list>
                  <v-list-item @click="sendBatch">Send Notification</v-list-item>
                  <v-list-item @click="deleteBatch">Archive</v-list-item>
                </v-list>
              </v-menu>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    rounded
                    v-show="canAddClient"
                    v-bind="attrs"
                    v-on="on"
                    class="batch-activator"
                    >Add Client<span class="fa-icon"
                      ><i class="fas fa-plus-circle"></i></span
                  ></v-btn>
                </template>
                <v-list>
                  <v-list-item @click="addNewClient">Add Single</v-list-item>
                  <v-list-item @click="addNewBatch">Add Batch</v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
        </template>
        <template slot="item" slot-scope="props" v-if="!tableLoading">
          <tr v-if="!isMobile" :key="`desktop-${getItemId(props.item)}`">
            <td>
              <v-checkbox
                :input-value="props.isSelected"
                style="margin: 0; padding: 0"
                hide-details
                @click="props.select(!props.isSelected)"
              >
              </v-checkbox>
            </td>
            <td v-for="col in tableColumns" :key="col" :width="col.width">
              <div v-if="!col.isActionColumn">
                <span
                  :class="col.classFn(props.item)"
                  @click="col.clickFn ? col.clickFn(props.item) : () => {}"
                >
                  {{ col.valueFn(props.item) }}
                </span>
              </div>
              <div v-else class="action-column">
                <v-menu
                  left
                  nudge-left="15"
                  nudge-bottom="25"
                  v-if="canPerformActions(props.item)"
                  min-width="125"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span class="actions" v-bind="attrs" v-on="on">
                      <v-icon small>fas fa-ellipsis-h</v-icon>
                    </span>
                  </template>
                  <v-list class="menu">
                    <div v-for="action in col.actions" :key="action">
                      <v-list-item
                        v-if="
                          !action.isDivider && action.conditionFn(props.item)
                        "
                        @click="action.clickFn(props.item)"
                      >
                        <v-list-item-title class="menu-item-wrapper" :class="{ disabled: action.disabledFn && action.disabledFn() }">
                          <v-icon x-small :key="props.item.status">
                            {{ action.iconFn(props.item) }}
                          </v-icon>
                          <span class="label">
                            {{ action.labelFn(props.item) }}
                          </span>
                        </v-list-item-title>
                      </v-list-item>
                      <div
                        class="divider"
                        v-if="action.isDivider && canShowDivider(props.item)"
                      ></div>
                    </div>
                  </v-list>
                </v-menu>
              </div>
            </td>
          </tr>
          <tr v-else :key="`mobile-${getItemId(props.item)}`">
            <td>
              <v-checkbox
                :input-value="props.isSelected"
                style="margin: 0; padding: 0"
                hide-details
                @click="props.select(!props.isSelected)"
              >
              </v-checkbox>
              <ul class="flex-content">
                <li class="flex-item" v-for="col in tableColumns" :key="col">
                  <div v-if="!col.isActionColumn">
                    <span class="mobile-label" v-if="col.dataLabel">
                      {{ col.dataLabel }}:
                    </span>
                    <span
                      :class="col.classFn(props.item)"
                      @click="col.clickFn ? col.clickFn(props.item) : () => {}"
                    >
                      {{ col.valueFn(props.item) }}
                    </span>
                  </div>
                  <div v-else class="actions-container">
                    <v-bottom-sheet v-if="canPerformActions(props.item)">
                      <template v-slot:activator="{ on, attrs }">
                        <span class="actions" v-bind="attrs" v-on="on">
                          <v-icon small>fas fa-ellipsis-h</v-icon>
                        </span>
                      </template>
                      <v-sheet height="auto">
                        <v-list class="sheet">
                          <div v-for="action in col.actions" :key="action">
                            <v-list-item
                              v-if="
                                !action.isDivider &&
                                action.conditionFn(props.item)
                              "
                              @click="action.clickFn(props.item)"
                            >
                              <v-list-item-title class="menu-item-wrapper" :class="{ disabled: action.disabledFn && action.disabledFn() }">
                                <v-icon x-small :key="props.item.status">
                                  {{ action.iconFn(props.item) }}
                                </v-icon>
                                <span class="label">
                                  {{ action.labelFn(props.item) }}
                                </span>
                              </v-list-item-title>
                            </v-list-item>
                            <div
                              class="divider"
                              v-if="
                                action.isDivider && canShowDivider(props.item)
                              "
                            ></div>
                          </div>
                          <div class="bottom-spacer"></div>
                        </v-list>
                      </v-sheet>
                    </v-bottom-sheet>
                  </div>
                </li>
              </ul>
            </td>
          </tr>
        </template>
        <div class="no-results" slot="no-results" :value="true">
          Your search/filter found no results.
        </div>
        <div class="no-data" slot="no-data" :value="true">
          No clients found.
          <a v-if="canAddClient" @click="addNewClient()">Add a new client</a>.
        </div>
      </v-data-table>
    </v-layout>
  </div>
</template>

<script>
import Themable from '@/common/mixins/themable.mixin';
import {
  MENUS,
  CLIENT_STATUSES,
  FETCH_CLIENT_STATUSES,
  CLIENT_STATUS_LABELS,
} from '@/common/constants';
import {
  GET_CLIENTS,
  GET_DRAFT_CLIENTS,
  GET_CLIENT,
  EDIT_CLIENT,
  DELETE_CLIENT,
  DELETE_CLIENT_BATCH,
  RESTORE_CLIENT,
  ARCHIVE_CLIENT,
  SEND_CLIENT_BATCH,
  SET_WORKING_CLIENT,
  IMPERSONATE_CLIENT,
  GET_REVIEW_ENVELOPE_URL,
  GET_CLIENT_URL,
  RESEND_CLIENT_TEXT,
  RETRY_CLIENT,
} from '../../../stores/actions.type';
import {
  NOTIFICATION_TYPES,
  NotificationService,
} from '@/services/notification.service';
import AdvisorClientMixin from '@/common/mixins/advisor.client.mixin';
import DataTableMixin from '@/common/mixins/datatable.mixin';
import { PermissionService, PERMISSIONS } from '@/services/permission.service';
import { dom } from '@fortawesome/fontawesome-svg-core';
import { mapGetters } from 'vuex';
import _ from 'lodash';

export default {
  data: () => ({
    selected: [],
    search: '',
    filterStatus: null,
    isMobile: false,
    clients: [],
    tableLoading: false,
    fetchMode: FETCH_CLIENT_STATUSES.ACTIVE,
    fetchStatuses: FETCH_CLIENT_STATUSES,
    pollingInterval: null,
    isPolling: false,
    pollingDelay: 5000, // 5 seconds
    urlDialog: false,
    clientUrls: null,
    pagination: {
      page: 1,
      itemsPerPage: 15,
      pageCount: 0,
      itemsLength: 0,
      sortBy: [],
      sortDesc: [],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: false
    },
    statuses: [],
    MENUS: MENUS,
  }),

  mixins: [Themable, AdvisorClientMixin, DataTableMixin],

  computed: {
    ...mapGetters(['currentCompany']),
    /* Table Header Definitions */
    headers() {
      const cols = [
        {
          text: 'Client',
          value: 'name',
          sortable: true,
        },
        {
          text: 'Primary Advisor',
          value: 'rep',
          sortable: true,
        },
        {
          text: 'Status',
          value: 'status',
          sortable: true,
        },
        {
          text: 'Interview Updated',
          value: 'lastContactActivityAt',
          sortable: true,
        },
        {
          text: 'OnBord',
          value: 'completionPercent',
          sortable: true,
        },
        {
          text: 'Actions',
          sortable: false,
          align: 'end',
        },
      ];

      return cols;
    },

    /* Table Column Definitions */
    tableColumns() {
      const cols = [
        {
          width: '24%',
          valueFn: (item) => {
            return item.name;
          },
          clickFn: (item) => {
            this.editClient(item);
          },
          classFn: (item) => {
            return `edit-client-link faux-anchor ${
              !this.canEditClient(item) ? 'disabled' : ''
            }`;
          },
        },
        {
          width: '19%',
          dataLabel: 'Primary Advisor',
          valueFn: (item) => {
            return item.rep;
          },
          classFn: () => {
            return '';
          },
        },
        {
          width: '20%',
          dataLabel: 'Status',
          valueFn: (item) => {
            if (item.isArchived) {
              return 'Archived';
            }

            if (
              item.isOnbordComplete &&
              item.status === CLIENT_STATUSES.SIGNED
            ) {
              return 'Signed';
            }

            if (!item.signatureRequested && item.isOnbordComplete) {
              return 'Complete';
            }

            return item.status === CLIENT_STATUSES.PENDING_DOCUSIGN_REVIEW
              ? 'Review Envelope'
              : item.status;
          },
          classFn: (item) => {
            let clz = item.status;
            switch (item.status) {
              case CLIENT_STATUSES.CLIENT_COMPLETE:
                clz = 'ClientComplete';
                break;
              case CLIENT_STATUSES.PENDING_DOCUSIGN_REVIEW:
                clz = 'btn WaitingReview';
                break;
              case CLIENT_STATUSES.WAITING_FOR_SIGNATURE:
                clz = 'WaitingSignature';
                break;
              case CLIENT_STATUSES.FAILED:
                clz = 'Failed';
                break;
            }

            if (item.isArchived) return 'status-pill Archived';

            if (!item.signatureRequested && item.isOnbordComplete)
              clz = 'btn ClientComplete';

            return `status-pill ${clz}`;
          },
          clickFn: (item) => {
            if (item.status === CLIENT_STATUSES.PENDING_DOCUSIGN_REVIEW) {
              return this.reviewEnvelope(item);
            }
            return null;
          },
        },
        {
          width: '17%',
          dataLabel: 'Interview Updated',
          valueFn: (item) => {
            return item.lastContactActivityAt || '-';
          },
          classFn: () => {},
        },
        {
          width: '8%',
          dataLabel: 'OnBord',
          valueFn: (item) => {
            return `${item.completionPercent || 0}%`;
          },
          classFn: () => {
            return '';
          },
        },
        {
          width: '10%',
          isActionColumn: true,
          actions: [
            {
              conditionFn: (item) => {
                return this.canImpersonate(item);
              },
              clickFn: (item) => {
                this.impersonateClient(item);
              },
              labelFn: () => {
                return 'Impersonate';
              },
              iconFn: () => {
                return 'fas fa-glasses';
              },
            },
            {
              conditionFn: (item) => {
                return this.canRetry(item);
              },
              clickFn: (item) => {
                this.retryClient(item);
              },
              labelFn: () => {
                return 'Retry';
              },
              iconFn: () => {
                return 'fas fa-redo-alt';
              },
            },
            {
              conditionFn: (item) => {
                return this.canSendText(item);
              },
              clickFn: (item) => {
                const { status } = item
                if (status === CLIENT_STATUSES.SENT) {
                  this.resendText(item)
                } else {
                  if (!this.isSmsEnabled()) return;
                  this.sendText(item);
                }
              },
              labelFn: (item) => {
                return this.sendTextVerbiage(item);
              },
              iconFn: () => {
                return 'fas fa-redo';
              },
              disabledFn: () => {
                return !this.isSmsEnabled();
              },
            },
            {
              conditionFn: (item) => {
                return this.canEditClient(item);
              },
              clickFn: (item) => {
                this.editClient(item);
              },
              labelFn: (item) => {
                return this.editTextVerbiage(item);
              },
              iconFn: (item) => {
                return this.editIcon(item);
              },
            },
            {
              conditionFn: (item) => {
                return this.canReviewEnvelope(item);
              },
              clickFn: (item) => {
                this.reviewEnvelope(item);
              },
              labelFn: () => {
                return 'Review Envelope';
              },
              iconFn: () => {
                return 'fas fa-envelope';
              },
            },
            {
              conditionFn: (item) => {
                return this.canRestore(item);
              },
              clickFn: (item) => {
                this.restoreClient(item);
              },
              labelFn: () => {
                return 'Restore';
              },
              iconFn: () => {
                return 'fas fa-trash-restore';
              },
            },
            {
              conditionFn: (item) => {
                return item.status === CLIENT_STATUSES.SENT;
              },
              clickFn: (item) => {
                this.getClientUrl(item);
              },
              labelFn: () => {
                return 'Copy URL';
              },
              iconFn: () => {
                return 'fas fa-link';
              },
            },
            {
              isDivider: true,
            },
            {
              conditionFn: (item) => {
                return this.canDeleteClient(item);
              },
              clickFn: (item) => {
                this.deleteClient(item);
              },
              labelFn: (item) => {
                return this.archiveTextVerbiage(item);
              },
              iconFn: () => {
                return 'fas fa-trash';
              },
            },
          ],
        },
      ];

      return cols;
    },

    canAddClient() {
      return PermissionService.hasPermission(PERMISSIONS.CLIENT.ADD_CLIENT);
    },
  },

  methods: {
    /* Check if SMS notifications are enabled for the organization */
    isSmsEnabled() {
      return this.currentCompany && this.currentCompany.smsNotificationsEnabled;
    },

    getItemId(item) {
      if (!item) return;
      return `${item.id}_${item.contactType}`;
    },
    transitionClients() {
      this.$router
        .push({ name: MENUS.TRANSITIONS.TRANSITION_TYPE.id })
        .catch(() => {});
    },

    /* 
      Reset Items on Search Clear - forces the table to re-draw
      Safari Mobile seems to have issues with not re-rendering icons
      when repainting the table rows. This is a hack to force the entire
      table to be re-drawn
    */
    resetItems() {
      this.tableLoading = true;
      this.$nextTick(() => setTimeout(() => (this.tableLoading = false)), 500);
    },

    /* Determine Mobile vs Desktop view */
    onResize() {
      this.isMobile = window.innerWidth < 769;
    },

    /* Is Client In a locked State - cannot be altered */
    isClientLocked(client) {
      if (!client) {
        return false;
      }

      const { status } = client;
      const signatureRequested =
        status === CLIENT_STATUSES.WAITING_FOR_SIGNATURE ||
        status === CLIENT_STATUSES.PENDING_DOCUSIGN_REVIEW;
      const completed = status === CLIENT_STATUSES.CLIENT_COMPLETE;

      return signatureRequested || completed;
    },

    /* Clear the User Status filter */
    clearStatusFilter() {
      this.filterStatus = null;
      this.$refs.statusFilter.blur();
    },

    /* Initiate New Client Flow */
    addNewClient() {
      // Wipe any working client - we are starting fresh
      this.$store.dispatch(SET_WORKING_CLIENT, null).then(() => {
        // Route to initial screen
        this.$router
          .push({ name: MENUS.ADVISOR.CLIENT.CLIENT_SELECTION.id })
          .catch(() => {});
      });
    },

    addNewBatch() {
      this.$router.push({ name: MENUS.ADD_CLIENT_BATCH.id }).catch(() => {});
    },

    /* Archived clients can be restored */
    canRestore(client) {
      return client.isArchived;
    },

    /* Sent/Incomplete clients can be impersonated */
    canImpersonate(client) {
      const { status, isArchived } = client;
      const isSent = status === CLIENT_STATUSES.SENT;
      const isLocked = this.isClientLocked(client);

      return [isSent, !isLocked, !isArchived].reduce(
        (acc, curr) => acc && curr,
        true,
      );
    },

    /* Incomplete clients not in DRAFT status can have their welcome text (re)sent */
    canSendText(client) {
      const { canSend, status, isArchived } = client;
      const isDraft = status === CLIENT_STATUSES.DRAFT;
      const isSigned = status === CLIENT_STATUSES.SIGNED;
      const isCreating = status === CLIENT_STATUSES.CREATING;
      const isFailed = status === CLIENT_STATUSES.FAILED;
      const isLocked = this.isClientLocked(client);

      return [
        canSend,
        !isDraft,
        !isSigned,
        !isLocked,
        !isArchived,
        !isCreating,
        !isFailed
      ].reduce(
        (acc, curr) => acc && curr,
        true
      );
    },


    /* 
      Depending on what permissions the current user has, a client's
      initial can be edited as long as they are not in SENT status.
    */
    canEditClient(client) {
      const { EDIT_OWN_CLIENT, EDIT_ANY_CLIENT } = PERMISSIONS.CLIENT;

      const hasPermission = [EDIT_OWN_CLIENT, EDIT_ANY_CLIENT].some(
          (permission) => PermissionService.hasPermission(permission),
      );
      const { isArchived, status } = client;
      const isClientLocked = this.isClientLocked(client);
      const isSent = status === CLIENT_STATUSES.SENT;
      const isSigned = status === CLIENT_STATUSES.SIGNED;
      const isCreating = status === CLIENT_STATUSES.CREATING;
      const isFailed = status === CLIENT_STATUSES.FAILED;


      const canEdit = [
        hasPermission,
        !isArchived,
        !isClientLocked,
        !isSent,
        !isSigned,
        !isCreating,
        !isFailed,
      ].reduce((acc, curr) => acc && curr, true);

      return canEdit;
    },

    /* 
      Clients in PENDING_DOCUSIGN_REVIEW can have their envelope viewed
    */
    canReviewEnvelope(client) {
      const { status, isArchived } = client;
      const pendingReview = status === CLIENT_STATUSES.PENDING_DOCUSIGN_REVIEW;

      return pendingReview && !isArchived;
    },

    /* Check if client can be retried (Failed status) */
    canRetry(client) {
      const { status, isArchived } = client;
      return status === CLIENT_STATUSES.FAILED && !isArchived;
    },

    /* Retry a failed client */
    retryClient(client) {
      const clientId = client.id;

      this.$store
        .dispatch(RETRY_CLIENT, clientId)
        .then(() => {
          NotificationService.alert({
            type: NOTIFICATION_TYPES.SUCEESS,
            title: 'Success',
            message: 'Client retry request submitted successfully!',
            okTitle: 'Ok',
          });
          this.getClients();
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: 'Error retrying client!',
              okTitle: 'Ok',
            },
            response
          );
        });
    },

    /* 
      Depending on what permission the current user has, a client can be
      Deleted as long as it is not in OnBord Complete status
    */
    canDeleteClient(client) {
      const { DELETE_OWN_CLIENT, DELETE_ANY_CLIENT } = PERMISSIONS.CLIENT;
      const hasPermission = [DELETE_OWN_CLIENT, DELETE_ANY_CLIENT].some(
        (permission) => PermissionService.hasPermission(permission),
      );

      return hasPermission && !client.isArchived;
    },

    /* Helper method to determine whether to show a divider in the actions context menu */
    canShowDivider(client) {
      return (
        this.canDeleteClient(client) &&
        (this.canImpersonate(client) ||
          this.canSendText(client) ||
          this.canEditClient(client) ||
          this.canReviewEnvelope(client) ||
          this.canRetry(client))
      );
    },

    /* If any action is still applicable for the client we can show the actions context menu */
    canPerformActions(client) {
      return (
        this.canImpersonate(client) ||
        this.canSendText(client) ||
        this.canEditClient(client) ||
        this.canReviewEnvelope(client) ||
        this.canDeleteClient(client) ||
        this.canRetry(client) ||
        this.canRestore(client)
      );
    },

    /* Get send/resend verbiage based on client status */
    sendTextVerbiage(client) {
      return client.status === CLIENT_STATUSES.SENT
        ? 'Resend Notification'
        : 'Send Notification';
    },

    /* Get finish/edit verbiage based on client status */
    editTextVerbiage(client) {
      return client.status === CLIENT_STATUSES.DRAFT ? 'Finish' : 'Edit';
    },

    /* Get delete/archive verbiage based on client status */
    archiveTextVerbiage(client) {
      return client.isOnbordComplete ? 'Archive' : 'Delete';
    },

    /* Get edit icon based on client status */
    editIcon(client) {
      return client.status === CLIENT_STATUSES.DRAFT
        ? 'fas fa-external-link-alt'
        : 'fas fa-pencil';
    },

    /* Allow a user to impersonate a client and enter that client's interview */
    impersonateClient(client) {
      const payload = {
        id: client.id,
        relatesTo: client.contactType,
      };
      this.$store
        .dispatch(IMPERSONATE_CLIENT, payload)
        .then(({ token }) => {
          const routeData = this.$router.resolve({
            name: MENUS.INTERVIEW.AUTHENTICATE.id,
            params: { token },
          });

          // Open the URL in a new tab
          window.open(routeData.href, '_blank');
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: `Failed to impersonate client!`,
              okTitle: 'Ok',
              okMethod: () => {
                this.getClients();
              },
            },
            response,
          );
        })
        .finally(() => {});
    },
    // Send or resend a batch of welcome texts
    sendBatch() {
      // We use a set here so we don't send duplicate primary ids
      let clientIds = new Set();
      for (var client of this.selected) {
        if (this.canSendText(client)) {
          clientIds.add(client.id);
        }
      }
      if (clientIds.size === 0) {
        NotificationService.alert({
          type: NOTIFICATION_TYPES.ERROR,
          title: 'Error',
          message: `None of the selected clients are ready to send!`,
          okTitle: 'Ok',
        });
      } else {
        this.$store
          .dispatch(SEND_CLIENT_BATCH, Array.from(clientIds))
          .then((response) => {
            // Clear the selected items on successful batch operation
            this.selected.splice(0);
            NotificationService.alert({
              type: NOTIFICATION_TYPES.SUCCESS,
              title: 'Success',
              message: `${response.totalClientsCommunicated} of ${response.batchSize} Clients / Households Communicated Successfully!`,
              okTitle: 'Ok',
            });
          })
          .catch((response) => {
            NotificationService.alert(
              {
                type: NOTIFICATION_TYPES.ERROR,
                title: 'Error',
                message: `Failed to Notification batch!`,
                okTitle: 'Ok',
              },
              response,
            );
          })
          .finally(() => {
            this.getClients();
          });
      }
    },
    // Delete/Archive a set of OnBords
    deleteBatch() {
      // We use a set here so we don't send duplicate primary ids
      let ids = new Set();
      for (var client of this.selected) {
        if (this.canDeleteClient(client)) {
          ids.add(client.id);
        }
      }
      if (ids.size === 0) {
        NotificationService.alert({
          type: NOTIFICATION_TYPES.ERROR,
          title: 'Error',
          message: `None of the selected clients can be deleted!`,
          okTitle: 'Ok',
        });
      } else {
        this.$store
          .dispatch(DELETE_CLIENT_BATCH, Array.from(ids))
          .then((response) => {
            // Clear the selected items on successful batch operation
            this.selected.splice(0);
            NotificationService.alert({
              type: NOTIFICATION_TYPES.SUCEESS,
              title: 'Success',
              message: `${response.clientsModified} of ${response.batchSize} Clients / Households Archived Successfully!`,
              okTitle: 'Ok',
            });
          })
          .catch((response) => {
            NotificationService.alert(
              {
                type: NOTIFICATION_TYPES.ERROR,
                title: 'Error',
                message: 'Failed to archive the selected clients!',
                okTitle: 'Ok',
              },
              response,
            );
          })
          .finally(() => {
            this.getClients();
          });
      }
    },
    /* Send/Resend Welcome text to client */
    sendText(client) {
      const payload = {
        id: client.id,
        sendNow: true,
      };

      this.$store
        .dispatch(EDIT_CLIENT, payload)
        .then(() => {
          NotificationService.alert({
            type: NOTIFICATION_TYPES.SUCEESS,
            title: 'Success',
            message: `${this.sendTextVerbiage(client)} Successful!`,
            okTitle: 'Ok',
          });
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: `Failed to ${this.sendTextVerbiage(client)}!`,
              okTitle: 'Ok',
            },
            response,
          );
        })
        .finally(() => {
          this.getClients();
        });
    },
    resendText(client) {
      const params = {
        clientsId: client.id,
      };

      this.$store.dispatch(RESEND_CLIENT_TEXT, params)
        .then(() => {
          NotificationService.alert({
            type: NOTIFICATION_TYPES.SUCEESS,
            title: 'Success',
            message: `${this.sendTextVerbiage(client)} Successful!`,
            okTitle: 'Ok',
          });
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: `Failed to ${this.sendTextVerbiage(client)}!`,
              okTitle: 'Ok',
            },
            response,
          );
        })
        .finally(() => {
          this.getClients();
        });
    },

    /* Edit a Draft client */
    editClient(client) {
      if (!this.canEditClient(client)) {
        return;
      }

      const id = !client.primaryInterviewId
        ? client.id
        : client.primaryInterviewId;

      this.$store
        .dispatch(GET_CLIENT, id)
        .then((response) => {
          // Reconstitute the working client from backend data
          const workingClient = this.reconstituteClient({
            ...response,
            id: response._id,
          });

          this.$store.dispatch(SET_WORKING_CLIENT, workingClient).then(() => {
            this.$router.push({
              name: MENUS.ADVISOR.CLIENT.REVIEW_AND_FINISH.id,
            });
          });
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: `Error getting client ${client.name} !`,
              okTitle: 'Ok',
              okMethod: () => {
                this.getClients();
              },
            },
            response,
          );
        })
        .finally(() => {});
    },

    /* Get the DocuSign URL and redirect so that user can review envelope */
    reviewEnvelope(client) {
      const clientId = client.id;

      this.$store
        .dispatch(GET_REVIEW_ENVELOPE_URL, clientId)
        .then((url) => {
          window.open(url, '_blank');
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: 'Error accessing DocuSign Envelope!',
              okTitle: 'Ok',
            },
            response,
          );
        });
    },

    /* Actually Delete Client */
    doDelete(client) {
      const action = this.archiveTextVerbiage(client);
      const actionPast = action === 'Delete' ? 'Deleted' : 'Archived';
      const actionPlural = action === 'Delete' ? 'Deleting' : 'Archiving';

      const id = client.id;

      const actionFn = action === 'Delete' ? DELETE_CLIENT : ARCHIVE_CLIENT;
      this.$store
        .dispatch(actionFn, id)
        .then(() => {
          NotificationService.alert({
            type: NOTIFICATION_TYPES.SUCEESS,
            title: 'Success',
            message: `Client Successfully ${actionPast}!`,
            okTitle: 'Ok',
          });
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: `Error Deleting ${actionPlural}!`,
              okTitle: 'Ok',
            },
            response,
          );
        })
        .finally(() => {
          this.getClients();
        });
    },

    /* Delete Client requested. Confirm/Warn before deletion */
    deleteClient(client) {
      const action = this.archiveTextVerbiage(client);
      const actionPlural = action === 'Delete' ? 'Deleting' : 'Archiving';
      let msg = `Are you sure you want to ${action} this client?`;

      // Clients that are part of a household should be warned that
      // the linked client will also be deleted.
      if (client.belongsToHousehold) {
        msg =
          `This client is part of a household. ${actionPlural} this client will ${action} <strong>the household</strong>.<br><br>` +
          msg;
      }

      NotificationService.confirm({
        type: NOTIFICATION_TYPES.WARNING,
        message: msg,
        okTitle: "Yes I'm sure",
        okMethod: () => {
          this.doDelete(client);
        },
      });
    },

    /* Actually Restore Client */
    doRestore(client) {
      const id = client.primaryInterviewId
        ? client.primaryInterviewId
        : client.id;

      this.$store
        .dispatch(RESTORE_CLIENT, id)
        .then(() => {
          NotificationService.alert({
            type: NOTIFICATION_TYPES.SUCEESS,
            title: 'Success',
            message: 'Client Successfully Restored!',
            okTitle: 'Ok',
          });
        })
        .catch((response) => {
          NotificationService.alert(
            {
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Error',
              message: 'Error Restoring client!',
              okTitle: 'Ok',
            },
            response,
          );
        })
        .finally(() => {
          this.getClients();
        });
    },

    /* Restore Client requested. Confirm/Warn before deletion */
    restoreClient(client) {
      let msg = 'Are you sure you want to Restore this client?';

      // Clients that are part of a household should be warned that
      // the linked client will also be restored.
      if (client.primaryInterviewId || client.secondaryInterviewId) {
        msg =
          `This client is part of a household. Restoring this client will Restore <strong>${client.familyName}</strong>.<br><br>` +
          msg;
      }

      NotificationService.confirm({
        type: NOTIFICATION_TYPES.WARNING,
        message: msg,
        okTitle: "Yes I'm sure",
        okMethod: () => {
          this.doRestore(client);
        },
      });
    },

    /* Build API payload for client requests */
    buildApiPayload() {
      const params = {
        enrich: false,
        page: this.pagination.page,
        limit: this.pagination.itemsPerPage,
      };

      if (this.fetchMode !== this.fetchStatuses.DRAFT) {
        params.include = this.fetchMode;
      }

      if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
        params.sortBy = this.pagination.sortBy[0];
        params.sortDesc = this.pagination.sortDesc && this.pagination.sortDesc[0] ? true : false;
      }

      if (this.search && this.search.trim().length > 0) {
        params.search = this.search.trim();
      }

      if (this.filterStatus) {
        params.filterStatus = this.filterStatus;
      }

      return params;
    },

    /* Format client data with name and rep fields */
    formatClientData(clients) {
      return clients.map(client => ({
        ...client,
        name: `${client.lastName}, ${client.firstName}`,
        rep: client.repLastName ? `${client.repLastName}, ${client.repFirstName}` : '-'
      }));
    },

    /* Update pagination data from API response */
    updatePaginationData(response) {
      this.pagination.itemsLength = response.totalResults;
      this.pagination.pageCount = Math.ceil(response.totalResults / this.pagination.itemsPerPage);
    },

    /* Get the list of clients */
    async getClients() {
      this.stopPolling();
      this.tableLoading = true;
      this.clients = [];

      try {
        const payload = this.buildApiPayload();
        const action = this.fetchMode === this.fetchStatuses.DRAFT ? GET_DRAFT_CLIENTS : GET_CLIENTS;
        const response = await this.$store.dispatch(action, payload);
        
        const records = response.results || response.result || [];
        this.clients = this.formatClientData(records);
        this.updatePaginationData(response);
      } catch (response) {
        this.clients = [];
        NotificationService.alert({
          type: NOTIFICATION_TYPES.ERROR,
          title: 'Error',
          message: 'Error getting list of clients!',
          okTitle: 'Ok',
        }, response);
      } finally {
        this.tableLoading = false;
        this.startPolling();
        dom.watch();
      }
    },

    /* Reset pagination to page 1 and refetch data when search/filter changes */
    resetPaginationAndRefetch() {
      // Reset to first page when search/filter criteria change
      this.pagination.page = 1;
      this.getClients();
    },

    async pollClients() {
      if (!this.isPolling) return;

      try {
        const payload = this.buildApiPayload();
        const action =
          this.fetchMode === this.fetchStatuses.DRAFT
            ? GET_DRAFT_CLIENTS
            : GET_CLIENTS;
        const response = await this.$store.dispatch(action, payload);

        const records = response && (response.results || response.result);
        if (records && records.length > 0) {
          const newClients = this.formatClientData(records);
          
          // More efficient check for changes
          if (JSON.stringify(this.clients) !== JSON.stringify(newClients)) {
            this.clients = newClients;
            this.updatePaginationData(response);
          }
        } else if (this.clients.length > 0) {
          // If no records are returned, but we have clients, clear the list
          this.clients = [];
        }
      } catch (error) {
        console.error('Error polling clients:', error);
      }
    },

    scheduleNextPoll() {
      if (this.isPolling) {
        this.pollingInterval = setTimeout(async () => {
          await this.pollClients();
          this.scheduleNextPoll();
        }, this.pollingDelay);
      } else {
        console.log('Polling is stopped, not scheduling next poll');
      }
    },

    startPolling() {
      if (!this.isPolling) {
        this.isPolling = true;
        this.scheduleNextPoll();
      }
    },

    stopPolling() {
      this.isPolling = false;
      if (this.pollingInterval) {
        clearTimeout(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    getClientUrl(client) {
      this.urlDialog = true;
      this.clientUrls = null;

      this.$store.dispatch(GET_CLIENT_URL, client.id)
        .then((response) => {
          if (response) {
            this.clientUrls = response;
          } else {
            this.clientUrls = { primaryUrl: null, secondaryUrl: null };
          }
        })
        .catch((error) => {
          this.clientUrls = { primaryUrl: null, secondaryUrl: null };
          NotificationService.alert({
            type: NOTIFICATION_TYPES.ERROR,
            title: 'Error',
            message: 'Error getting client URLs!',
            okTitle: 'Ok',
            error
          });
        })
    },

    copyUrl(url) {
      navigator.clipboard.writeText(url).then(() => {
        NotificationService.alert({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: 'Success',
          message: 'Client URL copied to clipboard!',
          okTitle: 'Ok',
        });
      });
    },

    closeUrlDialog() {
      this.urlDialog = false;
      this.clientUrls = null;
    },

    /* Handle pagination changes without resetting to page 1 */
    handlePaginationChange() {
      this.getClients();
    },
  },

  /* Lifecycle Hook */
  created() {
    // Set initial static status lists
    this.statuses = Object.values(CLIENT_STATUSES)
      .filter((s) => s !== CLIENT_STATUSES.DRAFT)
      .map((status) => ({ value: status, text: CLIENT_STATUS_LABELS[status] || status }));
    this.fetchStatuses = FETCH_CLIENT_STATUSES;
    this.MENUS = MENUS;
    // Debounced version of the pagination reset to avoid API calls on every keystroke
    this.debouncedResetPaginationAndRefetch = _.debounce(
      this.resetPaginationAndRefetch,
      300,
    );
    // Get client list
    this.getClients();
  },

  mounted() {},

  beforeDestroy() {
    this.stopPolling();

    // Cancel any pending debounce to avoid memory leaks
    if (
      this.debouncedResetPaginationAndRefetch &&
      this.debouncedResetPaginationAndRefetch.cancel
    ) {
      this.debouncedResetPaginationAndRefetch.cancel();
    }
  },

  watch: {
    // Watch for pagination changes that maintain current page
    'pagination.page': 'handlePaginationChange',
    'pagination.sortBy': 'handlePaginationChange',
    'pagination.sortDesc': 'handlePaginationChange',
    'fetchMode': 'handlePaginationChange',
    
    // Apply debounce to search so we don't hammer the API on each keystroke
    'search'() {
      this.debouncedResetPaginationAndRefetch();
    },
    'filterStatus': 'resetPaginationAndRefetch'
  }
};
</script>

<style lang="scss" scoped>
.mobile {
  color: var(--color-text-dark);
}

.prepend {
  padding-bottom: 10px;
  border-bottom: 1px solid var(--secondary-color);
  display: flex;
  justify-content: space-between;
  cursor: pointer;
}

.search-input {
  ::v-deep {
    .v-input__icon--clear {
      height: 16px;
    }

    .fa-times-circle {
      font-size: 16px;
      margin-right: 8px;
    }
  }
}

.actions {
  padding: 2px 4px;
  line-height: 16px;

  &[aria-expanded='true'] {
    background-color: var(--secondary-color);
    border-radius: 4px;

    ::v-deep .v-icon {
      color: var(--primary-color);
    }
  }
}

.menu {
  background-color: var(--color-white);
  min-width: 120px;

  ::v-deep {
    .v-list-item {
      min-height: 24px !important;
      padding: 0;
    }
  }
}

.menu-item-wrapper {
  padding-left: 10px;
  color: var(--color-text) !important;
  font-size: 14px !important;
  font-weight: 700;
  margin-bottom: 0;
  padding: 8px 12px;
  margin: 0 4px;

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
    
    &:hover {
      background-color: transparent;
      text-decoration: none;
    }
  }

  ::v-deep {
    .v-icon {
      font-weight: 700;
      color: var(--color-text) !important;
      //margin-right: 8px;
      margin-top: -2px;
    }
  }

  &:hover {
    color: var(--primary-color) !important;
    background-color: var(--input-bg-color);
    text-decoration: underline;

    ::v-deep .v-icon {
      color: var(--primary-color) !important;
    }
  }

  .label {
    padding-left: 8px;
  }
}

.sheet {
  background-color: var(--color-white);

  ::v-deep {
    .v-list-item {
      min-height: 24px !important;
      padding: 0;
    }
  }

  .menu-item-wrapper {
    font-size: 16px !important;
    padding: 12px;
  }

  .bottom-spacer {
    height: 25px;
  }
}

.status-col {
  max-width: 275px;
}

.o-status-col {
  max-width: 275px;
}

.action-column {
  text-align: end !important;
}

button.theme--light.v-btn--has-bg.batch-activator {
  background-color: var(--primary-color);
  color: var(--color-white);
  padding: 22px 20px;
  margin-right: 5px;
  text-transform: none;
  font-size: 16px;
}

.edit-client-link {
  color: var(--color-text);
  font-weight: 700;

  &:hover {
    color: var(--primary-color);
  }
}

.flex-content {
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  position: relative;

  .actions-container {
    position: absolute;
    top: 0;
    right: 0;
    line-height: normal;
  }

  .flex-item {
    padding: 5px;
    line-height: 24px;
    font-size: 16px;

    .edit-client-link {
      padding-right: 30px;
      display: inline-block;
      line-height: normal;
    }

    .mobile-label {
      font-weight: 400;
      margin-right: 10px;
    }
  }
}

.search {
  margin-right: 12px;
}

.divider {
  height: 1px;
  margin: 8px;
  background-color: var(--secondary-color);
}

.client-table {
  font-weight: 500;
  color: var(--color-text);

  .disposition-switch {
    ::v-deep label {
      font-size: 14px !important;
    }
  }

  .status-pill {
    padding: 4px 15px;
    border-radius: 30px;
    line-height: 24px;
    font-size: 14px;

    &.Draft,
    &.Ready {
      background-color: hsla(0, 0%, 72.7%, 0.16);
      color: var(--label-text);
    }

    &.Sent {
      background-color: rgba(30, 59, 112, 0.15);
      color: var(--primary-color);
    }

    &.WaitingSignature {
      background-color: rgba(252, 180, 0, 0.25);
      color: var(--primary-color);
    }

    &.WaitingReview {
      display: inline-block;
      background-color: transparent;
      color: var(--color-text);
      font-weight: 700;

      &:hover {
        background-color: var(--input-bg-color);
        text-decoration: underline;
        color: var(--primary-color);
      }
    }

    &.DownloadDocuments {
      display: inline-block;
      background-color: transparent;
      color: var(--color-text);
      font-weight: 700;

      &:hover {
        background-color: var(--input-bg-color);
        text-decoration: underline;
        color: var(--primary-color);
      }
    }

    &.ClientComplete,
    &.Signed {
      background-color: rgba(52, 182, 56, 0.25);
      color: var(--primary-color);
    }

    &.Rejected,
    &.Archived,
    &.Failed {
      background-color: rgba(232, 44, 44, 0.25);
      color: var(--primary-color);
    }
  }

  .filter-action-row {
    padding-bottom: 12px;
  }

  .weight-300 {
    font-weight: 300;
  }

  .font-fam-ui-serif {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  }

  .edit-client-link.disabled {
    text-decoration: none;
    color: var(--color-text);

    &:hover {
      color: var(--color-text);
      cursor: text;
    }
  }
}

.url-item {
  background-color: var(--input-bg-color);
  padding: 12px;
  border-radius: 4px;

  .url-label {
    font-weight: 700;
    margin-bottom: 8px;
    color: var(--color-text);
  }

  .url-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    word-break: break-all;

    .url-text {
      flex: 1;
      padding-right: 12px;
    }
  }
}

.no-urls {
  text-align: center;
  padding: 20px;
  color: var(--color-text);
  font-weight: 500;
}
@media screen and (max-width: 769px) {
  .filter-action-row {
    flex-direction: column;

    .status-col,
    .o-status-col {
      max-width: 100%;
      padding: 4px 12px;
    }

    .button-area {
      justify-content: flex-start;
    }
  }
}
</style>
